<template>
  <!-- Fixed bottom-right toast stack for newly arrived critical
       notifications. Watches the store's `toastQueue` for items the
       listener pushed in, animates them in, and auto-dismisses each
       after the configured timeout. Click-to-navigate uses the same
       link payload as the bell dropdown so the two surfaces stay
       behaviorally consistent. -->
  <Teleport to="body">
    <div class="fixed bottom-4 right-4 z-[60] flex flex-col gap-2 items-end pointer-events-none w-full max-w-sm">
      <TransitionGroup
        tag="div"
        class="flex flex-col gap-2 items-end"
        enter-active-class="transition duration-200 ease-out"
        enter-from-class="opacity-0 translate-y-2 scale-95"
        enter-to-class="opacity-100 translate-y-0 scale-100"
        leave-active-class="transition duration-150 ease-in"
        leave-from-class="opacity-100 translate-y-0"
        leave-to-class="opacity-0 translate-y-2"
      >
        <div
          v-for="toast in active"
          :key="toast.id"
          class="pointer-events-auto w-full rounded-lg border border-red-300/60 dark:border-red-500/40 bg-white dark:bg-slate-900 shadow-2xl ring-1 ring-black/5 overflow-hidden cursor-pointer"
          @click="onActivate(toast)"
        >
          <!-- Severity stripe — red for critical (the only severity
               that produces toasts today). Kept as a small leading
               band so the panel still reads as a normal card if we
               later widen toasts to warning/info. -->
          <div class="flex">
            <div :class="['w-1 shrink-0', stripeClass(toast.severity)]"></div>
            <div class="flex-1 px-4 py-3">
              <div class="flex items-start justify-between gap-3">
                <div class="min-w-0">
                  <div class="flex items-center gap-2 mb-1">
                    <span :class="badgeClass(toast.severity)" class="px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wide">
                      {{ toast.severity }}
                    </span>
                    <span class="text-[10px] uppercase tracking-wider text-gray-500">{{ toastTypeLabel(toast.type) }}</span>
                  </div>
                  <p class="text-sm font-semibold text-gray-900 dark:text-white">{{ toast.title }}</p>
                  <p class="text-xs text-gray-600 dark:text-gray-400 mt-0.5 line-clamp-2">{{ toast.body }}</p>
                </div>
                <button
                  @click.stop="dismissToast(toast.id)"
                  class="p-1 -m-1 rounded text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 shrink-0"
                  title="Dismiss toast"
                >
                  <svg class="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" /></svg>
                </button>
              </div>
              <!-- Progress bar — visualizes the auto-dismiss timer.
                   Reset per-toast via the inline transition. -->
              <div class="mt-2 h-0.5 rounded-full overflow-hidden bg-gray-100 dark:bg-white/5">
                <div
                  :class="['h-full', stripeClass(toast.severity)]"
                  :style="{ animation: `pulsar-toast-progress ${TOAST_TIMEOUT_MS}ms linear forwards` }"
                ></div>
              </div>
            </div>
          </div>
        </div>
      </TransitionGroup>
    </div>
  </Teleport>
</template>

<script setup lang="ts">
import type { AppNotification, NotificationSeverity, NotificationType } from '~/stores/notificationsStore'

/** How long a toast stays on screen before auto-dismissing. Long
 *  enough for the operator to read body + click through; short enough
 *  to not pile up if a wave of criticals arrive. */
const TOAST_TIMEOUT_MS = 6000

/** Max simultaneous toasts. Anything over this stays in the queue
 *  (still in the bell dropdown) and pops in as older toasts dismiss. */
const MAX_VISIBLE = 3

const router = useRouter()
const store = useNotificationsStore()
const { markRead } = useNotifications()

interface ActiveToast extends AppNotification {
  /** Per-toast timeout handle — used so manual dismiss cancels the
   *  scheduled auto-dismiss. */
  timeoutId: ReturnType<typeof setTimeout>
}

const active = ref<ActiveToast[]>([])

function pumpQueue() {
  while (active.value.length < MAX_VISIBLE) {
    const next = store.consumeToast()
    if (!next) break
    const toast: ActiveToast = {
      ...next,
      timeoutId: setTimeout(() => removeToast(next.id), TOAST_TIMEOUT_MS),
    }
    active.value.push(toast)
  }
}

function removeToast(id: string) {
  const idx = active.value.findIndex(t => t.id === id)
  if (idx === -1) return
  clearTimeout(active.value[idx].timeoutId)
  active.value.splice(idx, 1)
  // Pump again — a free slot may now have a queued toast to surface.
  pumpQueue()
}

function dismissToast(id: string) {
  // Toast dismissal is presentation-only — it removes the popup but
  // does NOT mark the notification read. Operator can still find it
  // in the bell. (Marking read on auto-dismiss would silently flush
  // unread state, which violates principle of least surprise.)
  removeToast(id)
}

async function onActivate(toast: ActiveToast) {
  await markRead(toast.id)
  removeToast(toast.id)
  if (toast.link?.path) {
    router.push({ path: toast.link.path, query: toast.link.query || {} })
  }
}

// Watch the queue length — every time the listener pushes new items,
// pump them through. Polling on a watch keeps the surface lightweight
// vs. wiring a custom event bus, and the queue itself is small (only
// criticals; usually 0 or 1 in flight).
watch(() => store.toastQueue.length, (n) => {
  if (n > 0) pumpQueue()
}, { immediate: true })

onBeforeUnmount(() => {
  for (const t of active.value) clearTimeout(t.timeoutId)
  active.value = []
})

// ---------------------------------------------------------------------------
// Styling helpers
// ---------------------------------------------------------------------------

function stripeClass(s: NotificationSeverity): string {
  return ({
    critical: 'bg-red-500',
    warning: 'bg-yellow-500',
    info: 'bg-blue-500',
  })[s]
}
function badgeClass(s: NotificationSeverity): string {
  return ({
    critical: 'bg-red-100 dark:bg-red-500/20 text-red-700 dark:text-red-300',
    warning: 'bg-yellow-100 dark:bg-yellow-500/20 text-yellow-700 dark:text-yellow-300',
    info: 'bg-blue-100 dark:bg-blue-500/20 text-blue-700 dark:text-blue-300',
  })[s]
}
function toastTypeLabel(t: NotificationType): string {
  return ({
    cost_anomaly: 'Cost anomaly',
    long_running_query: 'Long-running query',
    account_message: 'Account message',
  })[t] || 'Notification'
}
</script>

<style>
/* Keyframes live outside <style scoped> so they're addressable from
   the inline `animation:` shorthand in the template. The animation
   shrinks the progress bar from full → empty in TOAST_TIMEOUT_MS. */
@keyframes pulsar-toast-progress {
  from { width: 100%; }
  to   { width: 0%;   }
}
</style>
